import { fetchPackages } from 'Api/AccountApi';
import { getDppSkus, getDppSkusByUserType } from 'Api/DppApi';
import {
  getAprPriceTestSkus,
  getAprPriceTestVariant,
  getJanPriceTestSkus,
  getJanPriceTestVariant,
  getJunePriceTestSkus,
  getJunePriceTestVariant,
  getMayPriceTestSkus,
  getMayPriceTestVariant,
  getApr2025PriceTestSkus,
  getApr2025PriceTestVariant,
  isDefaultPlanFlow,
  isOctPriceTest,
  selectDefaultPlan,
} from 'Utils/Analytics/Amplitude';
import {
  getFreeSheerIdPlanFromType,
  isSheerIdFreePlan,
  isFirstResponderChurned,
  isMilitaryChurned,
  isSheerIdPlan,
  isEligibleSheerIdUpgradePlan,
} from 'Utils/SheerIdHelpers';
import {
  DISABLE_SHIPPING_ADDRESS_PROMOS,
  FOUNDERS_CUTOFF_DATE,
  FOUNDERS_SERVICE_ID,
  FIRST_MONTH_FREE_PROMO,
  FIRST_MONTH_99C_PROMO,
  VA_INITIAL_FREE_YEAR_ID,
  VA_MONTHLY_ID,
  VA_1_YEAR_ID,
  VA_2_YEAR_ID,
  VA_ALT_1_YEAR_ID,
  SHEER_FIRST_RESPONDER_CHURNED_PACKAGE_ID,
  SHEER_MILITARY_CHURNED_PACKAGE_ID,
} from './Constants';
import giftPlans from 'Config/GiftPlans.json';
import { DPP_STAGING_USERTYPE_PREFIX, getUserType } from './Dpp';
import { isVAFreePlan, isVeteransAdvPlan } from './VeteransAdvHelpers';
import { getDefaultConfig } from './Store';

export const isFounderServiceId = (serviceId) => serviceId === FOUNDERS_SERVICE_ID;
export const isFoundingMember = (serviceId, startDate) =>
  isFounderServiceId(serviceId) && startDate < FOUNDERS_CUTOFF_DATE;

// utils
export function getFrequencyLabel({ duration, period }) {
  if (!duration || !period) return '';

  if (period === 'NoOfYears') {
    return duration > 1 ? `${duration} yrs` : `yr`;
  }
  if (period === 'NoOfMonths') {
    return duration > 1 ? `${duration} mos` : `mo`;
  }
  return '';
}

export const getPlanSelectFrequencyLabel = (plan) => {
  let per = '/';
  let time = plan.period === 'NoOfYears' ? 'yr' : 'mo';
  let plural = plan.duration > 1 ? 's' : '';

  let result = `/${plan.duration > 1 ? plan.duration : ''}${time}${plural}`;

  if (plan.hasPrePayPromotion) {
    per = ' for ';
    result = `${per}${plan.duration}${time}${plural}`;
  }
  return result;
};

export const getRenewalPriceFrequencyLabel = (nextBillingAmount, planFrequency) => {
  if (!nextBillingAmount || nextBillingAmount.trim().length < 1) return null;
  switch (planFrequency) {
    case 'mo':
      return `${nextBillingAmount}/month`;
    case 'yr':
      return `${nextBillingAmount}/year`;
    case '2 yrs':
      return `${nextBillingAmount} for 2 years`;
  }
  return nextBillingAmount;
};

export const getPlanDisplayPrice = (planPrice) => {
  if (!planPrice) return '';

  const price = planPrice.toFixed(2);
  const [dollars, cents] = price.split('.');

  return `$${dollars}${Number(cents) > 0 ? `.${cents}` : ''}`;
};

//Not to be used in Billing History screens
export const getSimplifiedPlanDisplayName = (plan) => {
  const dppPlanName = plan?.dppContent?.planName;
  let planName = 'Monthly';
  if (!dppPlanName) {
    if (plan.period === 'NoOfYears') {
      if (plan.duration > 1) {
        planName = '2 Year';
      } else {
        planName = 'Yearly';
      }
    }
  }
  if (plan.isGiftPlan) {
    planName = plan.planName;
  }

  return dppPlanName || planName;
};

export const getActivePlanSimplifiedName = (plans = [], activePlanId = '') => {
  const activePlan = plans.filter((plan) => plan.planId === activePlanId)[0];
  if (activePlan) {
    return getSimplifiedPlanDisplayName(activePlan);
  }
  return null;
};

export const getPlanButtonText = (hasSubscription, plan) => {
  let linkText = 'Select Plan';

  if (plan?.dppContent?.ctaText) {
    linkText = plan.dppContent.ctaText;
  } else if (is7DayFreeTrial(plan)) {
    linkText = 'Start Your 7-Day Free Trial';
  } else if (is30DayFreeTrial(plan)) {
    linkText = 'Start Your 30-Day Free Trial';
  } else if (isFreeTrial(plan)) {
    linkText = 'Start Your Free Trial';
  }

  if (hasSubscription) {
    linkText = plan?.dppContent?.ctaTextExistingUser || 'Update Plan';
  }

  return linkText;
};

const prePayPromotion = ({ promotionCategory = '' }) => promotionCategory === 'PrePay';

// isFreeTrail from the BE means is a default promo, nothing else
const findDefaultPromo = ({ isFreeTrail }) => isFreeTrail;

/* The isFreeTrail field from Evergent is unreliable - e.g. the 1STMONTH99C (1st month for 99cents)
   has isFreeTrail = true. This function helps determine if it is actually a free trial
   for FE display purposes  */
export const isFreeTrial = (plan) => {
  return plan?.hasDefaultPromo && plan?.defaultPromo && !(plan?.defaultPromo.amount > 0);
};

export const is7DayFreeTrial = (plan) => {
  return plan?.hasDefaultPromo && plan?.defaultPromo?.period === 'NoOfDays' && plan?.defaultPromo?.duration === 8;
};

export const is30DayFreeTrial = (plan) => {
  return plan?.hasDefaultPromo && plan?.defaultPromo?.period === 'NoOfDays' && plan?.defaultPromo?.duration === 30;
};

const promotionalItem = ({ PromotionalItem = [] }) => {
  const hasPromotionalItems = Array.isArray(PromotionalItem) && PromotionalItem.length;
  if (hasPromotionalItems) {
    // temporary disabling shipping form. (roku promo have digital merch, so it does not require a shipping address)
    // Evergent is going to provide a flag at promo level to reflect non-digital merchandise
    const validPromotionalItems = PromotionalItem.some(
      (promo) => !DISABLE_SHIPPING_ADDRESS_PROMOS.includes(promo.serviceId)
    );
    return validPromotionalItems;
  }
  return false;
};

export const mapPackages = (packages = [], dppPlans = [], userType) => {
  const mapPlanWithDpp = (pkg) => {
    const pkgDpp = dppPlans.find((dpp) => {
      return dpp.planId === pkg.pkgMsg.packageID;
    });
    return mapPlan({ ...pkg, dpp: pkgDpp, userType });
  };

  return packages.map(mapPlanWithDpp);
};

export const mapPlan = ({ promoMsg, pkgMsg = {}, dpp = {}, userType }) => {
  const { appEnv } = getDefaultConfig();

  let promos = promoMsg || [];
  // promo fields from DPP are newPromo, existingPromo, churnedPromo
  // in staging env:
  // we remove the `staging` prefix from user type since it's not included in the promo fields listed above.
  // instead, we fetch `STAGING-` versions of the same plans from DPP (via them assigned to the staging user types)
  // so that there's no overlap with prod (i.e., we can maintain a promo on prod while testing a different one on
  // staging for the same plan).
  const promoUserType =
    appEnv === 'staging' && userType.startsWith(DPP_STAGING_USERTYPE_PREFIX)
      ? userType.slice(DPP_STAGING_USERTYPE_PREFIX.length).toLowerCase()
      : userType;
  promos = promos.filter((promo) => {
    return dpp[`${promoUserType}Promo`] === promo.promotionID;
  });

  const hasPromotionalItems = promos.some(promotionalItem);
  const hasPrePayPromotion = promos.some(prePayPromotion);
  const hasDefaultPromo = promos.some(findDefaultPromo);
  const prePayPromo = promos.find(prePayPromotion); // amount,duration,period,promotionID
  const defaultPromo = promos.find(findDefaultPromo); // displayName,nextBillingDateTime,promotionID
  const hasMerchandise = hasPromotionalItems && hasPrePayPromotion;

  const { appId, packageID, tier, displayName, packageName, bbDescription, retailPrice, duration, period } = pkgMsg;

  const planName = displayName;
  const planPrice = hasPrePayPromotion ? prePayPromo.amount : retailPrice;
  const planFrequencyText = hasPrePayPromotion
    ? getFrequencyLabel(prePayPromo)
    : getFrequencyLabel({ duration, period });
  let promotionID = hasPrePayPromotion ? prePayPromo.promotionID : '';
  let frequency = hasPrePayPromotion ? prePayPromo : { duration, period };
  if ((isSheerIdFreePlan(packageID) || isVAFreePlan(packageID)) && defaultPromo) {
    promotionID = defaultPromo.promotionID || '';
    frequency = defaultPromo;
  }

  return {
    appId,
    promotionID,
    packageID,
    planId: packageID,
    tier,
    planName,
    packageName,
    duration: frequency.duration,
    period: frequency.period,
    planDescription: bbDescription,
    planPrice,
    hasMerchandise,
    planFrequencyText,
    defaultPromo,
    hasDefaultPromo,
    hasPrePayPromotion,
  };
};

export const addAmplitudeExperiments = async (dppPlans) => {
  let plans = dppPlans;
  try {
    if (getApr2025PriceTestVariant()) {
      plans = await getApr2025PriceTestSkus(dppPlans);
    } else if (getJunePriceTestVariant()) {
      plans = await getJunePriceTestSkus(dppPlans);
    } else if (getJanPriceTestVariant()) {
      plans = await getJanPriceTestSkus(dppPlans);
    } else if (getAprPriceTestVariant()) {
      plans = await getAprPriceTestSkus(dppPlans);
    } else if (getMayPriceTestVariant()) {
      plans = await getMayPriceTestSkus(dppPlans);
    } else if (isOctPriceTest()) {
      //check if has both skus want to replace and save their position
      const results = {};
      plans.forEach(({ planId }, idx) => {
        if (planId === 'FOXNATION1M-WEB2') {
          results['FOXNATION1M-WEB3'] = idx;
        } else if (planId === 'FOXNATION12M-WEB') {
          results['FOXNATION12M-WEB2'] = idx;
        }
      });

      //replace the regular plans with the new experiment plans
      if (Object.keys(results).length === 2) {
        const [newMonthly, newYearly] = await getDppSkus(['FOXNATION1M-WEB3', 'FOXNATION12M-WEB2']);
        plans[results['FOXNATION1M-WEB3']] = newMonthly;
        plans[results['FOXNATION12M-WEB2']] = newYearly;
      }
    }
  } finally {
    return plans;
  }
};

export const isCurrentOrNextPlan = (subscription, packageID) => {
  const nextSubId = subscription?.nextSubscription?.serviceID;
  return subscription.activeSubscriptionId === packageID || nextSubId === packageID;
};

export const missingCurrentNextPlanIds = (subscription, planIds) => {
  let missingIds = [];

  const nextSubId = subscription?.nextSubscription?.serviceID;

  if (!planIds.includes(subscription.activeSubscriptionId)) {
    missingIds = [subscription.activeSubscriptionId];
  }
  if (nextSubId && !planIds.includes(nextSubId)) {
    missingIds = [...missingIds, nextSubId];
  }

  return missingIds;
};

export const getPlanIdsFromSkus = (plans) => {
  return plans.map(({ planId }) => planId);
};

export const handleSheerIdPlans = async (subscription) => {
  const allSheerIdPlans = await fetchPackages();
  const backendPlans = getAvailableSheerIdPlans(subscription, allSheerIdPlans);

  const planIds = getPlanIdsFromSkus(backendPlans.map(({ pkgMsg: { packageID } }) => ({ planId: packageID })));
  const dppPlans = await getDppSkus(planIds);

  return { backendPlans, dppPlans };
};

export const handleVeteransAdvPlans = async (subscription) => {
  let planIdsToFetch = [VA_INITIAL_FREE_YEAR_ID];
  const { activeSubscriptionId, isChurnedUser, restartChurnedUserDetails } = subscription;
  let prevPlanId = activeSubscriptionId;

  if (
    activeSubscriptionId === VA_ALT_1_YEAR_ID ||
    (isChurnedUser && isVeteransAdvPlan(restartChurnedUserDetails?.churnedPlan?.packageID))
  ) {
    planIdsToFetch = [VA_ALT_1_YEAR_ID];
  } else if (isVAFreePlan(activeSubscriptionId)) {
    planIdsToFetch = [];
  } else {
    switch (activeSubscriptionId) {
      case VA_MONTHLY_ID:
        planIdsToFetch = [VA_ALT_1_YEAR_ID];
        break;
      case VA_1_YEAR_ID:
        planIdsToFetch = [];
        break;
      case VA_2_YEAR_ID:
        planIdsToFetch = [];
        break;
    }
  }

  // ensure that the user's current plan is included if it's not already
  if (prevPlanId && !planIdsToFetch.includes(prevPlanId)) {
    planIdsToFetch.push(prevPlanId);
  }

  const backendPlans = await fetchPackages(planIdsToFetch);
  const dppPlans = await getDppSkus(planIdsToFetch);

  return { backendPlans, dppPlans };
};

const removeSameTierPlans = (plans, dominantPlanIds = []) => {
  const dominantPlanTiers = {};

  plans.forEach(({ pkgMsg: { packageID, tier } }) => {
    if (dominantPlanIds.includes(packageID)) {
      dominantPlanTiers[tier] = true;
    }
  });

  return plans.filter(({ pkgMsg: { packageID, tier } }) => {
    const isDominantPlan = dominantPlanIds.some((id) => id === packageID);
    if (isDominantPlan) return true;
    if (dominantPlanTiers[tier]) return false;
    return true;
  });
};

export const handleGeneralPlans = async (subscription, planToSelect) => {
  let backendPlans = [];
  const userType = getUserType(subscription.hasActiveSubscription, subscription.isChurnedUser);
  let dppPlans = await getDppSkusByUserType(userType);
  dppPlans = await addAmplitudeExperiments(dppPlans);

  let planIds = getPlanIdsFromSkus(dppPlans);

  //CURRENT SUBSCRIBERS
  if (subscription.hasSubscription) {
    let additionalIds = [];
    //if current plan is a gift card plan, no additional ids from current or next plan
    if (subscription.purchaseType !== 'gift') {
      additionalIds = missingCurrentNextPlanIds(subscription, planIds);
    }

    const additionalPlanStrings = await getDppSkus(additionalIds);

    dppPlans = [...dppPlans, ...additionalPlanStrings];

    planIds = getPlanIdsFromSkus(dppPlans);

    backendPlans = await fetchPackages(planIds);

    //Shouldn't show other plans of same tier of current plan
    backendPlans = removeSameTierPlans(backendPlans, [subscription.activeSubscriptionId]);
  } else {
    backendPlans = await fetchPackages(planIds);
  }

  return { backendPlans, dppPlans };
};

export const getAvailableSheerIdPlans = (subscription, plans) => {
  let plansToDisplay = plans;

  //EXISTING SUBSCRIBERS:
  //always show current plan and, if there is one, next plan
  if (subscription.hasActiveSubscription) {
    //if has an active first responder or military plan, only show eligible upgrade plans
    if (isSheerIdPlan(subscription.activeSubscriptionId)) {
      plansToDisplay = plansToDisplay.filter(
        ({ pkgMsg }) =>
          isEligibleSheerIdUpgradePlan(subscription, pkgMsg) || isCurrentOrNextPlan(subscription, pkgMsg.packageID)
      );
      //if newly verified in sheerid, show the free sheerid plan:
    } else if (getFreeSheerIdPlanFromType()) {
      plansToDisplay = plansToDisplay.filter(
        ({ pkgMsg: { packageID } }) =>
          packageID === getFreeSheerIdPlanFromType() || isCurrentOrNextPlan(subscription, packageID)
      );
    }

    //NEW AND CHURNED USERS:
  } else {
    //if had a first responder plan previously, only show eligible first responder upgrade plans
    if (isFirstResponderChurned(subscription.isChurnedUser, subscription.restartChurnedUserDetails)) {
      plansToDisplay = plansToDisplay.filter(
        ({ pkgMsg: { packageID } }) => packageID === SHEER_FIRST_RESPONDER_CHURNED_PACKAGE_ID
      );
      //if had a military plan previously, only show eligible military upgrade plans
    } else if (isMilitaryChurned(subscription.isChurnedUser, subscription.restartChurnedUserDetails)) {
      plansToDisplay = plansToDisplay.filter(
        ({ pkgMsg: { packageID } }) => packageID === SHEER_MILITARY_CHURNED_PACKAGE_ID
      );
      //if newly verified in sheerid and hasn't got a free sheerid plan yet, show the free sheerid plan:
    } else if (getFreeSheerIdPlanFromType()) {
      plansToDisplay = plansToDisplay.filter(({ pkgMsg: { packageID } }) => packageID === getFreeSheerIdPlanFromType());
    }
  }

  return plansToDisplay;
};

export const handlePlanToSelect = (planToSelect, plans, activeSubscriptionId, nextSubscription) => {
  let { planId = '' } = getPlanFromList(planToSelect, plans, activeSubscriptionId, nextSubscription);

  if (!planId && isDefaultPlanFlow()) {
    planId = selectDefaultPlan(plans, activeSubscriptionId);
  }

  return planId;
};

export const getPlanFromList = (query = '', plans = [], activeSubscriptionId = '', nextSubscription = {}) => {
  const matchedPlan = plans.find((plan) => {
    const { planName = '', packageID = '' } = plan;
    //Don't match if no query of if existing user already has the plan
    if (!query || activeSubscriptionId === packageID || nextSubscription?.serviceID === packageID) return false;
    const preSelectedPlan = query.toUpperCase();
    return planName.toUpperCase().startsWith(preSelectedPlan) || preSelectedPlan === packageID.toUpperCase();
  });
  return matchedPlan || {};
};

export const isFreeMonthPromoPlanActive = (selectedPlan) => {
  const { defaultPromo } = selectedPlan || {};
  if (defaultPromo) {
    return defaultPromo.promotionID === FIRST_MONTH_FREE_PROMO;
  }
  return false;
};

/*
  Helper to reduce plans (from /packages) and DPP Plan copy/images
  Needs to maintain order of plans from BE /packages (BE sorts by tier). so order is determined by backendPlans
  If there is somehow a mismatch of SKUs in each list, will only includes SKUs that are in both lists.
*/
export const getPlansWithDppContent = ({ backendPlans, dppPlans }) => {
  const planStringsMap = {};
  dppPlans.forEach((planString) => {
    planStringsMap[planString.planId] = planString;
  });

  let result = [];

  backendPlans.forEach((plan) => {
    const dppContent = planStringsMap[plan.planId];
    const dppContentOverride = plan.dppContentOverride || {};
    if (dppContent) {
      result.push({ ...plan, dppContent: { ...dppContent, ...dppContentOverride } });
    }
  });

  return result;
};

export const isMonthlyPromoPlanActive = (selectedPlan) => {
  const { defaultPromo } = selectedPlan || {};
  if (defaultPromo) {
    return defaultPromo.promotionID === FIRST_MONTH_99C_PROMO;
  }
  return false;
};

export const getCurrentOfferText = (subscription) => {
  const { promotions } = subscription;

  if (!promotions || promotions.length < 1) {
    return null;
  }

  return subscription.promotions[0].displayName;
};

export const addGiftPlanInfo = (plans, subscription) => {
  const giftPlan = giftPlans[subscription.activeSubscriptionId];

  if (giftPlan) {
    //mocking essential data would get from backend /packages endpoint
    const mockBackendInfo = {
      isGiftPlan: true,
      appId: 'foxnation',
      packageID: subscription.activeSubscriptionId,
      packageName: giftPlan.purchasedTitle,
      planId: subscription.activeSubscriptionId,
      planName: giftPlan.purchasedTitle,
      tier: 0,
    };
    //mocking essential data would get from DPP
    const mockDppInfo = {
      planId: subscription.activeSubscriptionId,
      frequencyLabel: 'false',
    };

    plans.backendPlans.unshift(mockBackendInfo);
    plans.dppPlans.unshift(mockDppInfo);
  }

  return plans;
};
